import * as React from 'react';
import { useState, useEffect, useMemo } from 'react';
import AvailabilitiesService from '../services/AvailabilitiesService';
import EmployeeService from '../../employees/services/EmployeeService';
import globalErrorHandler from '../../../core/services/GlobalErrorHandler';
import dialogUtil from '../../../shared/utils/dialog';
import { FaUsers, FaClock, FaCalendarAlt } from 'react-icons/fa';
import { personalakteStyles as ps, colors, shadows } from '../../employees/styles/personalakteStyles';

/**
 * Interface for availability range
 */
interface AvailabilityRange {
  start: number;
  end: number;
}

/**
 * Interface for day availability data
 */
interface DayAvailability {
  weekday: number;
  all_day: boolean;
  not_at_all: boolean;
  ranges: AvailabilityRange[];
}

/**
 * Interface for range selection state
 */
interface RangeSelecting {
  day: number;
  startHour: number | null;
}

/**
 * Interface for weekday definition
 */
interface WeekdayDefinition {
  key: number;
  label: string;
}

/**
 * Interface for employee object
 */
interface Employee {
  id: number;
  user?: number;
  first_name?: string;
  last_name?: string;
  [key: string]: any; // Allow for additional properties that might come from the API
}

/**
 * Interface for bulk save payload
 */
interface BulkSavePayload {
  employee: number;
  availabilities: DayAvailability[];
}

/**
 * Props interface for AvailabilitiesRanges component
 */
interface AvailabilitiesRangesProps {
  employees?: Employee[];
  activeEmployeeId?: number | null;
  setActiveEmployeeId?: (id: number | null) => void;
}

// Montag=0 .. Sonntag=6
const WEEKDAYS: WeekdayDefinition[] = [
  { key: 0, label: 'Monday' },
  { key: 1, label: 'Tuesday' },
  { key: 2, label: 'Wednesday' },
  { key: 3, label: 'Thursday' },
  { key: 4, label: 'Friday' },
  { key: 5, label: 'Saturday' },
  { key: 6, label: 'Sunday' },
];

export default function AvailabilitiesRanges({
  employees: propEmployees = [],
  activeEmployeeId: propActiveEmployeeId = null,
  setActiveEmployeeId: propSetActiveEmployeeId = null
}: AvailabilitiesRangesProps = {}): React.JSX.Element {
  const [loading, setLoading] = useState<boolean>(false);
  const [errMsg, setErrMsg] = useState<string>('');
  const [localEmployees, setLocalEmployees] = useState<Employee[]>([]);

  // Use prop employees if provided, otherwise use local state
  const allEmployees = propEmployees.length > 0 ? propEmployees : localEmployees;

  // Employee filter - use prop if provided, otherwise local state
  const [localActiveEmployeeId, setLocalActiveEmployeeId] = useState<number | null>(null);
  const activeEmployeeId = propActiveEmployeeId ?? localActiveEmployeeId;
  const setActiveEmployeeId = propSetActiveEmployeeId ?? setLocalActiveEmployeeId;

  const [activeEmployee, setActiveEmployee] = useState<Employee | null>(null);

  // Aggregated availability data for overview
  const [allAvailabilities, setAllAvailabilities] = useState<Map<number, DayAvailability[]>>(new Map());
  const [loadingOverview, setLoadingOverview] = useState<boolean>(false);

  /**
   * days = Array(7).each => {
   *   weekday: 0..6,
   *   all_day: boolean,
   *   not_at_all: boolean,
   *   ranges: [ {start, end}, ... ]
   * }
   */
  const [days, setDays] = useState<DayAvailability[]>([]);

  /**
   * 2-Klick-Auswahl => { day: -1|0..6, startHour: number|null }
   */
  const [rangeSelecting, setRangeSelecting] = useState<RangeSelecting>({ day: -1, startHour: null });

  useEffect(() => {
    // Only load employees if not provided via props
    if (propEmployees.length > 0) return;

    async function loadEmployees(): Promise<void> {
      try {
        const employees = await EmployeeService.fetchAllEmployees();
        setLocalEmployees(employees);
      } catch (err: any) {
        const handled = globalErrorHandler.handle(err);
        if (!handled) {
          globalErrorHandler.handleUnexpected(err);
        }
      }
    }
    loadEmployees();
  }, [propEmployees.length]);

  useEffect(() => {
    function syncActiveEmployee(): void {
      if (!activeEmployeeId || allEmployees.length === 0) {
        setActiveEmployee(null);
        return;
      }
      const employee = allEmployees.find(emp => emp.id === activeEmployeeId);
      setActiveEmployee(employee || null);
    }
    syncActiveEmployee();
  }, [activeEmployeeId, allEmployees]);

  useEffect(() => {
    async function loadAvailabilities(empId: number): Promise<void> {
      try {
        setLoading(true);
        setErrMsg('');
        const apiData = await AvailabilitiesService.fetchAvailabilities(empId);

        // Initialize all 7 weekdays (0-6), using API data where available
        const allDays: DayAvailability[] = Array.from({ length: 7 }, (_, weekday) => {
          const existingDay = apiData.find(d => d.weekday === weekday);
          return existingDay || {
            weekday,
            all_day: false,
            not_at_all: false,
            ranges: []
          };
        });

        setDays(allDays);
      } catch (err: any) {
        const handled = globalErrorHandler.handle(err);
        if (!handled) {
          globalErrorHandler.handleUnexpected(err);
        }
      } finally {
        setLoading(false);
      }
    }
    if (!activeEmployee?.id) return;
    loadAvailabilities(activeEmployee.id);
  }, [activeEmployee?.id]);

  // Load all availabilities for overview when no employee selected
  useEffect(() => {
    async function loadAllAvailabilities(): Promise<void> {
      if (activeEmployee?.id || allEmployees.length === 0) return;

      setLoadingOverview(true);
      const availMap = new Map<number, DayAvailability[]>();

      for (const emp of allEmployees) {
        try {
          const apiData = await AvailabilitiesService.fetchAvailabilities(emp.id);
          if (apiData && apiData.length > 0) {
            availMap.set(emp.id, apiData);
          }
        } catch {
          // Skip employees without availabilities
        }
      }

      setAllAvailabilities(availMap);
      setLoadingOverview(false);
    }

    loadAllAvailabilities();
  }, [activeEmployee?.id, allEmployees]);

  // Calculate aggregated stats per weekday
  const weekdayStats = useMemo(() => {
    const stats = WEEKDAYS.map(wd => {
      let totalHours = 0;
      let employeeCount = 0;
      const timeSlots: { start: number; end: number; count: number }[] = [];

      // Initialize hour counts
      const hourCounts = Array(24).fill(0);

      allAvailabilities.forEach((empAvail) => {
        const dayData = empAvail.find(d => d.weekday === wd.key);
        if (dayData) {
          if (dayData.all_day) {
            employeeCount++;
            totalHours += 24;
            for (let h = 0; h < 24; h++) hourCounts[h]++;
          } else if (!dayData.not_at_all && dayData.ranges.length > 0) {
            employeeCount++;
            dayData.ranges.forEach(r => {
              const s = Math.min(r.start, r.end);
              const e = Math.max(r.start, r.end);
              totalHours += (e - s + 1);
              for (let h = s; h <= e; h++) {
                if (h < 24) hourCounts[h]++;
              }
            });
          }
        }
      });

      // Find peak hours (hours with most availability)
      const maxCount = Math.max(...hourCounts);
      const peakHours: number[] = [];
      hourCounts.forEach((count, hour) => {
        if (count === maxCount && count > 0) peakHours.push(hour);
      });

      return {
        weekday: wd.key,
        label: wd.label,
        totalHours,
        employeeCount,
        hourCounts,
        peakHours,
        maxCount
      };
    });

    return stats;
  }, [allAvailabilities]);


  // Prüfen, ob "hour" in dayItem.ranges liegt
  function isHourInRanges(dayItem: DayAvailability, hour: number): boolean {
    for (const rng of dayItem.ranges) {
      const s = Math.min(rng.start, rng.end);
      const e = Math.max(rng.start, rng.end);
      if (hour >= s && hour <= e) return true;
    }
    return false;
  }

  // Toggle "All Day"
  function handleAllDay(weekday: number): void {
    setDays(prev => prev.map(d => {
      if (d.weekday !== weekday) return d;

      const newVal = !d.all_day;
      if (newVal) {
        // => all_day => ranges=[{0..23}], not_at_all=false
        return {
          ...d,
          all_day: true,
          not_at_all: false,
          ranges: [{ start: 0, end: 23 }]
        };
      } else {
        return {
          ...d,
          all_day: false,
          not_at_all: false,
          ranges: []
        };
      }
    }));
  }

  // Toggle "NotAtAll"
  function handleNotAtAll(weekday: number): void {
    setDays(prev => prev.map(d => {
      if (d.weekday !== weekday) return d;
      const newVal = !d.not_at_all;
      if (newVal) {
        return {
          ...d,
          not_at_all: true,
          all_day: false,
          ranges: []
        };
      } else {
        return {
          ...d,
          not_at_all: false
        };
      }
    }));
  }

  // Klick auf Stunde => 2-Klick Logik
  function handleHourClick(dayItem: DayAvailability, hour: number): void {
    if (dayItem.all_day) {
      // war all_day => erst ausschalten
      handleAllDay(dayItem.weekday);
      return;
    }
    if (dayItem.not_at_all) {
      handleNotAtAll(dayItem.weekday);
      return;
    }

    // Already in ranges => remove
    if (isHourInRanges(dayItem, hour)) {
      removeHourFromRanges(dayItem.weekday, hour);
      return;
    }

    // 2-Klick
    if (rangeSelecting.day < 0) {
      // 1. Klick
      setRangeSelecting({ day: dayItem.weekday, startHour: hour });
    } else if (rangeSelecting.day === dayItem.weekday) {
      // 2. Klick, gleicher Wochentag
      let s = rangeSelecting.startHour!;
      let e = hour;
      if (e < s) {
        const tmp = e; e = s; s = tmp;
      }
      addRange(dayItem.weekday, s, e);
      setRangeSelecting({ day: -1, startHour: null });
    } else {
      // user klickt auf anderen day => single hour range
      setRangeSelecting({ day: -1, startHour: null });
      addRange(dayItem.weekday, hour, hour);
    }
  }

  function addRange(weekday: number, start: number, end: number): void {
    setDays(prev => prev.map(d => {
      if (d.weekday !== weekday) return d;
      const newRanges = [...d.ranges, { start, end }];
      return { ...d, ranges: newRanges };
    }));
  }

  function removeHourFromRanges(weekday: number, hour: number): void {
    setDays(prev => prev.map(d => {
      if (d.weekday !== weekday) return d;
      const newRanges = d.ranges.filter(r => {
        const s = Math.min(r.start, r.end);
        const e = Math.max(r.start, r.end);
        return !(hour >= s && hour <= e);
      });
      return { ...d, ranges: newRanges };
    }));
  }

  // Speichern => Bulk
  async function handleSave(): Promise<void> {
    if (!activeEmployee?.id) return;
    try {
      setLoading(true);
      setErrMsg('');

      const payload: BulkSavePayload = {
        employee: activeEmployee.id,
        availabilities: days.map(d => ({
          weekday: d.weekday,
          all_day: d.all_day,
          not_at_all: d.not_at_all,
          ranges: d.ranges.map(r => {
            const s = Math.min(r.start, r.end);
            const e = Math.max(r.start, r.end);
            return { start: s, end: e };
          })
        }))
      };
      await AvailabilitiesService.saveAvailabilities(activeEmployee.id, payload);
      dialogUtil.showToast({ message: "Availabilities saved successfully!", type: "success" });
    } catch (err: any) {
      const handled = globalErrorHandler.handle(err);
      if (!handled) {
        globalErrorHandler.handleUnexpected(err);
      }
    } finally {
      setLoading(false);
    }
  }

  const buttonBaseStyle: React.CSSProperties = {
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    padding: '8px',
    fontSize: '0.75rem',
    width: '100%',
    transition: 'filter 0.2s ease'
  };

  function onButtonHoverEnter(e: React.MouseEvent<HTMLButtonElement>): void {
    e.currentTarget.style.filter = 'brightness(1.2)';
  }

  function onButtonHoverLeave(e: React.MouseEvent<HTMLButtonElement>): void {
    e.currentTarget.style.filter = 'brightness(1.0)';
  }

  const allDayStyle = (isActive: boolean): React.CSSProperties => ({
    ...buttonBaseStyle,
    backgroundColor: isActive ? colors.navy : colors.bgMuted,
    color: isActive ? '#fff' : colors.textBody
  });

  const notAtAllStyle = (isActive: boolean): React.CSSProperties => ({
    ...buttonBaseStyle,
    backgroundColor: isActive ? colors.border : colors.bgMuted,
    color: colors.textBody
  });

  // Save-Button -> NB: wir wollen ihn top-right => 
  // => wir legen ein parent-DIV, justify-content: space-between, 
  //    so Title links, Button rechts
  const saveBtnStyle: React.CSSProperties = {
    ...ps.buttonPrimary,
    padding: '10px 16px',
    fontSize: '0.85rem',
  };

  // Falls kein Employee - zeige Übersicht
  if (!activeEmployee?.id) {
    const totalEmployeesWithAvail = allAvailabilities.size;
    const totalWeeklyHours = weekdayStats.reduce((sum, s) => sum + s.totalHours, 0);

    return (
      <div style={{
        padding: '16px',
        color: colors.textBody,
        fontSize: '0.9rem'
      }}>
        {loadingOverview && (
          <p style={{ color: colors.slate, fontSize: '12px', padding: '0 4px' }}>Lade Verfügbarkeitsdaten...</p>
        )}

        {/* Combined Header: Summary + Weekday Cards */}
        <div className="glass-container" style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '16px',
          padding: '16px'
        }}>
          {/* Summary Stats - Left Side */}
          <div style={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            gap: '12px',
            padding: '8px 16px 8px 8px',
            borderRight: '1px solid rgba(0, 0, 0, 0.06)',
            minWidth: '140px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'rgba(82, 113, 165, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaUsers size={14} color={colors.slate} />
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: colors.slate, lineHeight: 1 }}>
                  {totalEmployeesWithAvail}
                </div>
                <div style={{ fontSize: '9px', color: colors.textMuted, fontWeight: 500 }}>
                  Mitarbeiter
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'rgba(82, 113, 165, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaClock size={14} color={colors.slate} />
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: colors.slate, lineHeight: 1 }}>
                  {totalWeeklyHours}h
                </div>
                <div style={{ fontSize: '9px', color: colors.textMuted, fontWeight: 500 }}>
                  Std/Woche
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                background: 'rgba(82, 113, 165, 0.1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <FaCalendarAlt size={14} color={colors.slate} />
              </div>
              <div>
                <div style={{ fontSize: '18px', fontWeight: 700, color: colors.slate, lineHeight: 1 }}>
                  {totalEmployeesWithAvail > 0 ? Math.round(totalWeeklyHours / totalEmployeesWithAvail) : 0}h
                </div>
                <div style={{ fontSize: '9px', color: colors.textMuted, fontWeight: 500 }}>
                  Ø/Person
                </div>
              </div>
            </div>
          </div>

          {/* Weekday Cards - Right Side */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '8px',
            flex: 1
          }}>
            {weekdayStats.map(stat => {
              const isWeekend = stat.weekday >= 5;
              return (
                <div
                  key={stat.weekday}
                  style={{
                    padding: '10px 6px',
                    textAlign: 'center',
                    background: isWeekend ? 'rgba(82, 113, 165, 0.05)' : 'rgba(255, 255, 255, 0.4)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.04)',
                  }}
                >
                  <div style={{
                    fontSize: '10px',
                    fontWeight: 600,
                    color: colors.textMuted,
                    marginBottom: '6px',
                    textTransform: 'uppercase',
                    letterSpacing: '0.3px'
                  }}>
                    {stat.label.slice(0, 2)}
                  </div>

                  <div style={{
                    display: 'flex',
                    alignItems: 'baseline',
                    justifyContent: 'center',
                    gap: '3px',
                    marginBottom: '2px'
                  }}>
                    <span style={{ fontSize: '16px', fontWeight: 700, color: colors.slate }}>
                      {stat.employeeCount}
                    </span>
                    <span style={{ fontSize: '9px', color: colors.textLight }}>MA</span>
                    <span style={{ fontSize: '9px', color: '#cbd5e1', margin: '0 2px' }}>·</span>
                    <span style={{ fontSize: '12px', fontWeight: 600, color: colors.textBody }}>
                      {stat.totalHours}h
                    </span>
                  </div>

                  {/* Mini hour visualization - vertical bars */}
                  <div style={{
                    display: 'flex',
                    gap: '1px',
                    marginTop: '8px',
                    justifyContent: 'center',
                    alignItems: 'flex-end',
                    height: '24px'
                  }}>
                    {stat.hourCounts.slice(6, 22).map((count, idx) => {
                      const hour = idx + 6;
                      const intensity = stat.maxCount > 0 ? count / stat.maxCount : 0;
                      const barHeight = count > 0 ? 6 + intensity * 18 : 3;
                      return (
                        <div
                          key={hour}
                          title={`${hour}:00 - ${count} Mitarbeiter`}
                          style={{
                            width: '3px',
                            height: `${barHeight}px`,
                            borderRadius: '1px',
                            background: count > 0
                              ? `rgba(82, 113, 165, ${0.3 + intensity * 0.7})`
                              : 'rgba(0, 0, 0, 0.08)',
                          }}
                        />
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Time Slots Detail */}
        <div className="glass-container" style={{ padding: '16px', display: 'flex', gap: '16px' }}>
          {/* Left side - Hour labels (matching header summary width) */}
          <div style={{
            minWidth: '140px',
            paddingRight: '16px',
            borderRight: '1px solid rgba(0, 0, 0, 0.06)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <h4 style={{
              margin: '0 0 12px 0',
              fontSize: '11px',
              fontWeight: 600,
              color: colors.textBody,
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <FaCalendarAlt size={10} color={colors.slate} />
              Nach Stunden
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {Array.from({ length: 16 }, (_, i) => i + 6).map(hour => (
                <div key={hour} style={{
                  height: '22px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  paddingRight: '12px',
                  color: colors.textLight,
                  fontSize: '10px',
                  fontWeight: 500
                }}>
                  {hour}:00
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Grid (aligned with weekday cards) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
            gap: '8px',
            flex: 1
          }}>
            {weekdayStats.map(stat => (
              <div key={stat.weekday} style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
                {Array.from({ length: 16 }, (_, i) => i + 6).map(hour => {
                  const count = stat.hourCounts[hour];
                  const intensity = stat.maxCount > 0 ? count / stat.maxCount : 0;
                  return (
                    <div
                      key={`${stat.weekday}-${hour}`}
                      title={`${stat.label} ${hour}:00 - ${count} verfügbar`}
                      style={{
                        height: '22px',
                        borderRadius: '4px',
                        background: count > 0
                          ? `rgba(82, 113, 165, ${0.15 + intensity * 0.85})`
                          : 'rgba(0, 0, 0, 0.04)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: count > 0 && intensity > 0.5 ? 'white' : '#94a3b8',
                        fontSize: '9px',
                        fontWeight: count > 0 ? 600 : 400,
                        cursor: 'default'
                      }}
                    >
                      {count > 0 ? count : ''}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>

        {/* Hint */}
        <p style={{
          marginTop: '16px',
          fontSize: '11px',
          color: colors.textLight,
          textAlign: 'center'
        }}>
          Wähle einen Mitarbeiter in der linken Sidebar um individuelle Verfügbarkeiten zu bearbeiten.
        </p>
      </div>
    );
  }

  // Falls Fehler
  if (errMsg) {
    return <p style={{ color: colors.danger }}>Error: {errMsg}</p>;
  }

  // =============== RENDER ===============
  return (
    <div style={{
      margin: '0', padding: '20px',
      background: colors.bgPage,
      color: colors.navy
    }}>

      {/* Title + Save Button row */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '20px'
      }}>
        <h3 data-testid="availabilities-title" style={{ margin: 0, fontSize: '1.3rem' }}>
          Availabilities for {activeEmployee?.first_name} {activeEmployee?.last_name}
        </h3>

        <button
          data-testid="save-availabilities-button"
          onClick={handleSave}
          style={saveBtnStyle}
          onMouseEnter={onButtonHoverEnter}
          onMouseLeave={onButtonHoverLeave}
        >
          Save Availabilities
        </button>
      </div>

      {loading && <p data-testid="loading-message" style={{ color: colors.slate }}>Loading…</p>}

      {errMsg && <p data-testid="error-message" style={{ color: colors.danger }}>Error: {errMsg}</p>}

      <div style={{
        display: 'flex',
        gap: '16px',
        alignItems: 'flex-start',
        // -> columns fill entire width
        width: '100%',
        boxSizing: 'border-box'
      }}>
        {days.map(d => {
          const dayLabel = WEEKDAYS.find(x => x.key === d.weekday)?.label || '???';

          // Erzeuge 24 Hour-Boxen
          const hourBoxes = Array.from({ length: 24 }, (_, hour) => {
            const selected = isHourInRanges(d, hour);
            const isFirstClick = (
              rangeSelecting.day === d.weekday &&
              rangeSelecting.startHour === hour
            );

            // BG/Farbe
            let bg = colors.bgCard;
            let clr = colors.navy;

            if (d.all_day) {
              bg = colors.navy;
              clr = '#fff';
            } else if (d.not_at_all) {
              bg = colors.border;
              clr = colors.textBody;
            } else if (selected) {
              bg = colors.navy;
              clr = '#fff';
            } else if (isFirstClick) {
              bg = colors.slateDark;
              clr = '#fff';
            }

            return (
              <div
                key={hour}
                onClick={() => handleHourClick(d, hour)}
                style={{
                  width: '100%',           // neu: volle Spaltenbreite
                  height: '24px',
                  marginBottom: '4px',
                  backgroundColor: bg,
                  color: clr,
                  fontSize: '0.75rem',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  transition: 'background 0.2s ease'
                }}
              >
                {hour}
              </div>
            );
          });

          const handleClickAllDay = (): void => handleAllDay(d.weekday);
          const handleClickNotAtAll = (): void => handleNotAtAll(d.weekday);

          return (
            <div
              key={d.weekday}
              style={{
                background: colors.bgCard,
                borderRadius: '8px',
                padding: '8px',
                flex: '1',
                display: 'flex',
                flexDirection: 'column',
                border: `1px solid ${colors.border}`,
                minWidth: '120px'
              }}
            >
              <h4 style={{
                margin: '0 0 8px 0',
                fontSize: '1rem',
                textAlign: 'center'
              }}>
                {dayLabel}
              </h4>

              {/* Buttons - vertical stacked, full width */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '8px' }}>
                <button
                  style={allDayStyle(d.all_day)}
                  onClick={handleClickAllDay}
                  onMouseEnter={onButtonHoverEnter}
                  onMouseLeave={onButtonHoverLeave}
                >
                  All Day
                </button>
                <button
                  style={notAtAllStyle(d.not_at_all)}
                  onClick={handleClickNotAtAll}
                  onMouseEnter={onButtonHoverEnter}
                  onMouseLeave={onButtonHoverLeave}
                >
                  None
                </button>
              </div>

              {/* Hour Boxes */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                maxHeight: '650px',
                overflowY: 'auto'
              }}>
                {hourBoxes}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
} 